// Attach a global axios request interceptor that stamps every outgoing HTTP
// call with the customer/owner's currently-selected UI language. The backend
// middleware in server.py reads `X-Bookly-Locale` first (our own header —
// unambiguous, exactly matches the visible UI) and falls back to standard
// `Accept-Language` (for third-party clients).
//
// We read the language directly from `localStorage` on every request rather
// than from React state so the interceptor stays scope-aware (customer vs
// owner pref) without threading it through every axios call site. The
// storage keys mirror `i18n/config.js::STORAGE_KEYS`.

import axios from "axios";
import { STORAGE_KEYS, DEFAULT_LANGUAGE, LANGUAGE_MAP, OWNER_ROUTE_PREFIXES } from "@/i18n/config";

// SEC-002: Double-Submit Cookie CSRF token. The backend sets a non-httpOnly
// `bookly_csrf` cookie on every response; we must echo its value on every
// state-changing request so cross-origin attackers (who cannot read the cookie
// due to Same-Origin Policy) can't forge requests.
const CSRF_COOKIE = "bookly_csrf";
const CSRF_HEADER = "X-CSRF-Token";
function readCsrfToken() {
  try {
    const match = document.cookie.match(new RegExp("(?:^|; )" + CSRF_COOKIE + "=([^;]+)"));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function currentScope() {
  try {
    const p = window.location.pathname;
    return OWNER_ROUTE_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + "/"))
      ? "owner"
      : "customer";
  } catch {
    return "customer";
  }
}

function currentLanguage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[currentScope()]);
    if (raw && LANGUAGE_MAP[raw]) return raw;
  } catch {
    // ignore — private-mode / SSR
  }
  return DEFAULT_LANGUAGE;
}

let installed = false;

export function installLocaleAxiosInterceptor() {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    const lang = currentLanguage();
    config.headers = config.headers || {};
    // Always overwrite — a stale value from a previous language would send
    // out-of-sync error messages back.
    config.headers["X-Bookly-Locale"] = lang;
    // Non-destructive: only set Accept-Language if the caller hasn't set one.
    if (!config.headers["Accept-Language"]) {
      config.headers["Accept-Language"] = lang;
    }
    // Attach CSRF token on state-changing methods.
    const method = (config.method || "get").toLowerCase();
    if (method !== "get" && method !== "head" && method !== "options") {
      const token = readCsrfToken();
      if (token) config.headers[CSRF_HEADER] = token;
    }
    return config;
  });
}
